/**
 * DocumentsPanel — the files attached to one record (P23, migration 0104).
 * Used by the Documents tab on Person / Account / Policy and under the Agreement's
 * legal-documents grid. Design: `_MEMORY/decisions/2026-10-10_documents-and-photos-build.md`.
 *
 * Pattern A (§3g, Pedro 2026-10-10 "inside Edit mode"): Add document / Remove / Restore
 * STAGE into the page's queue (orange, "Pending Save") and are sent with the page's
 * Save; Discard drops them. Afterwards an RM's operations show blue ("Pending ·
 * Upload" / "Pending · Delete") until approved; Admin / Compliance ones apply at once.
 *
 * Rows:
 *   - documents on file, newest first: type (+ title), file (View in a new tab /
 *     Download), who added it and when;
 *   - uploads awaiting approval (blue; the requester and the approvers can open the
 *     file before the decision);
 *   - staged uploads (orange);
 *   - removed documents, behind "Show removed" (dimmed; Restore stages in Edit mode).
 * Every file read is logged by the server (DLP).
 */
import {
  ActionIcon, Badge, Button, FileInput, Group, Modal, Select, Stack, Switch, Table, Text,
  TextInput, Tooltip,
} from '@mantine/core';
import {
  IconArrowBackUp, IconDownload, IconEye, IconFilePlus, IconRotate, IconTrash, IconUpload,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';

import { ApiError } from '../api/client';
import {
  checkDocumentFile, documentsApi, DOCUMENT_ACCEPT, formatFileSize,
  type DocumentKindSpec, type DocumentOwnerType, type PendingUpload, type RecordDocument,
} from '../api/documents';
import type { UUID } from '../api/types';
import { useCurrentUser } from '../hooks/useAuth';
import {
  kindsFor, useDocumentKinds, useRecordDocuments,
  type DocumentStaging, type PendingDocumentOp,
} from '../hooks/useDocuments';
import { usePermissions } from '../hooks/usePermissions';
import { useAlertModal } from './AlertModalProvider';
import { DisabledTooltipButton } from './DisabledTooltipButton';
import { PendingCRBadge } from './PendingCRBadge';

function when(iso: string | null | undefined): string {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

/** The Add-document dialog. It STAGES (Pattern A): the file is sent on the page's Save. */
export function AddDocumentModal({
  opened, onClose, kinds, presetKind, onAdd,
}: {
  opened: boolean;
  onClose: () => void;
  /** The types allowed on this record. */
  kinds: DocumentKindSpec[];
  /** Fix the type (e.g. "IAA (signed)" from the Agreement grid's On-file column). */
  presetKind?: string | null;
  onAdd: (docKind: string, title: string | null, file: File) => void;
}) {
  const [docKind, setDocKind] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const kind = presetKind ?? docKind;
  const spec = kinds.find((k) => k.kind === kind);
  const fileError = file ? checkDocumentFile(file, !!spec?.images_only) : null;

  function reset() {
    setDocKind(null);
    setTitle('');
    setFile(null);
  }
  function close() { reset(); onClose(); }

  return (
    <Modal opened={opened} onClose={close} centered title={<Text fw={600}>Add document</Text>}>
      <Stack>
        <Select
          label="Document type"
          placeholder="Pick a type…"
          data={kinds.map((k) => ({ value: k.kind, label: k.label }))}
          value={kind}
          onChange={setDocKind}
          disabled={!!presetKind}
          allowDeselect={false}
          required
        />
        <TextInput
          label="Title (optional)"
          placeholder="e.g. utility bill, March"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
        />
        <FileInput
          label="File"
          description={spec?.images_only ? 'JPG or PNG, up to 10 MB' : 'PDF, JPG or PNG, up to 10 MB'}
          placeholder="Choose a file…"
          accept={spec?.images_only ? 'image/jpeg,image/png,.jpg,.jpeg,.png' : DOCUMENT_ACCEPT}
          value={file}
          onChange={setFile}
          error={fileError}
          leftSection={<IconUpload size={14} />}
          clearable
          required
        />
        <Text size="xs" c="dimmed">
          The document is uploaded when you save the page. An RM&apos;s upload is filed for approval.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={close}>Cancel</Button>
          <Button
            disabled={!kind || !file || !!fileError}
            onClick={() => {
              if (!kind || !file) return;
              onAdd(kind, title.trim() || null, file);
              close();
            }}
          >
            Add
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** View / Download icons for a stored document. */
export function DocumentFileActions({ doc }: { doc: RecordDocument }) {
  const { showAlert } = useAlertModal();
  return (
    <Group gap={4} wrap="nowrap">
      <Tooltip label="View (opens a new tab)" withArrow>
        <ActionIcon variant="subtle" aria-label={`View ${doc.filename}`}
          onClick={() => documentsApi.view(doc.id)}>
          <IconEye size={16} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Download" withArrow>
        <ActionIcon variant="subtle" aria-label={`Download ${doc.filename}`}
          onClick={() => {
            documentsApi.download(doc.id, doc.filename).catch((e) => showAlert({
              color: 'red', title: 'Download failed',
              message: e instanceof ApiError ? e.detail : 'Unexpected error.',
            }));
          }}>
          <IconDownload size={16} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

/** A pending upload as the shared blue badge ("Pending · Upload", linking to the request). */
export function PendingUploadBadge({ p }: { p: PendingUpload }) {
  return (
    <PendingCRBadge crs={[{
      id: p.change_request_id, kind: 'CREATE', requested_by: p.requested_by,
      requested_by_name: p.requested_by_name, requested_at: p.requested_at ?? '',
      op_label: p.op_label,
    }]} />
  );
}

interface Props {
  ownerType: DocumentOwnerType;
  ownerId: UUID;
  /** On a person: which types fit (ID for natural persons, LEI / RCBE for companies…). */
  personKind?: 'NATURAL' | 'LEGAL' | null;
  editMode: boolean;
  staging: DocumentStaging;
  /** Types not shown here at all. */
  excludeKinds?: readonly string[];
  /** Types listed here but added elsewhere (the person photo is changed in the header,
   *  where it is shrunk first; earlier photos still list here). */
  noAddKinds?: readonly string[];
}

export function DocumentsPanel({
  ownerType, ownerId, personKind, editMode, staging, excludeKinds = [], noAddKinds = [],
}: Props) {
  const { can } = usePermissions();
  const canWrite = can('record.write');
  const { user } = useCurrentUser();
  const isApprover = user?.role === 'admin' || user?.role === 'compliance';
  const kindsQ = useDocumentKinds();
  const docsQ = useRecordDocuments(ownerType, ownerId);
  const [addOpen, setAddOpen] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const { queue, stageUpload } = staging;

  const shown = (kind: string) => !excludeKinds.includes(kind);
  const kinds = kindsFor(kindsQ.data, ownerType, personKind)
    .filter((k) => shown(k.kind) && !noAddKinds.includes(k.kind));
  const all = (docsQ.data?.documents ?? []).filter((d) => shown(d.kind));
  const alive = all.filter((d) => !d.deleted_at);
  const removed = all.filter((d) => !!d.deleted_at);
  const pendingUploads = (docsQ.data?.pending_uploads ?? []).filter((p) => shown(p.kind));

  const staged = useMemo(() => {
    const byDoc = new Map<string, PendingDocumentOp>();
    const uploads: Extract<PendingDocumentOp, { kind: 'upload' }>[] = [];
    for (const op of queue.ops) {
      if (op.kind === 'upload') { if (shown(op.docKind)) uploads.push(op); } else byDoc.set(op.document_id, op);
    }
    return { byDoc, uploads };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue.ops, excludeKinds]);
  const labelOf = (kind: string) => kindsQ.data?.find((k) => k.kind === kind)?.label ?? kind;
  const active = canWrite && editMode;
  const empty = alive.length === 0 && pendingUploads.length === 0 && staged.uploads.length === 0;
  // Smoke 2026-10-11 (S7): with every document removed, the table must still render when
  // "Show removed" is on — the removed rows live inside it.
  const showTable = !empty || (showRemoved && removed.length > 0);

  const failed = (key: string) => queue.failedKeys.has(key) && (
    <Tooltip label={queue.failedReasons.get(key) ?? 'Failed'} withArrow multiline w={260}>
      <Badge color="red" variant="light" size="xs">Failed</Badge>
    </Tooltip>
  );

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Group gap="md">
          {removed.length > 0 && (
            <Switch size="xs" label={`Show removed (${removed.length})`} checked={showRemoved}
              onChange={(e) => setShowRemoved(e.currentTarget.checked)} />
          )}
        </Group>
        <DisabledTooltipButton
          size="xs" leftSection={<IconFilePlus size={14} />}
          disabledReason={!canWrite ? 'Read-only users cannot add documents.'
            : !editMode ? 'Enter Edit mode to add documents.' : null}
          onClick={() => setAddOpen(true)}
        >
          Add document
        </DisabledTooltipButton>
      </Group>

      {docsQ.isLoading && <Text size="sm" c="dimmed">Loading…</Text>}
      {!docsQ.isLoading && empty && (
        <Text size="sm" c="dimmed">
          {removed.length > 0
            ? `No current documents on file (${removed.length} removed — use "Show removed").`
            : 'No documents on file yet.'}
        </Text>
      )}

      {showTable && (
        <Table withTableBorder striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Document</Table.Th>
              <Table.Th>File</Table.Th>
              <Table.Th w={220}>Added</Table.Th>
              <Table.Th w={170} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {alive.map((d) => {
              const op = staged.byDoc.get(d.id);
              const removing = op?.kind === 'remove';
              return (
                <Table.Tr key={d.id} style={removing ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{d.kind_label}</Text>
                    {d.title && <Text size="xs" c="dimmed">{d.title}</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <DocumentFileActions doc={d} />
                      <Text size="xs" c="dimmed">{d.filename} · {formatFileSize(d.size_bytes)}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{[d.created_by_name, when(d.created_at)].filter(Boolean).join(' · ')}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap" justify="flex-end">
                      {removing && <Badge color="orange" variant="light" size="xs">Pending remove</Badge>}
                      {failed(d.id)}
                      <PendingCRBadge crs={d.pending_change_requests} />
                      {active && (op ? (
                        <Tooltip label="Undo staged change" withArrow>
                          <ActionIcon variant="light" color="gray" onClick={() => queue.unstage(d.id)}>
                            <IconArrowBackUp size={14} />
                          </ActionIcon>
                        </Tooltip>
                      ) : (
                        <Tooltip label="Remove (on Save)" withArrow>
                          <ActionIcon variant="light" color="red" aria-label={`Remove ${d.kind_label}`}
                            onClick={() => queue.stage({ kind: 'remove', document_id: d.id })}>
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      ))}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}

            {pendingUploads.map((p) => (
              <Table.Tr key={p.change_request_id}>
                <Table.Td>
                  <Text size="sm" fw={500}>{p.kind_label}</Text>
                  {p.title && <Text size="xs" c="dimmed">{p.title}</Text>}
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    {(isApprover || p.requested_by === user?.id) && (
                      <Tooltip label="View (opens a new tab)" withArrow>
                        <ActionIcon variant="subtle" onClick={() => documentsApi.viewPending(p.change_request_id)}>
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    <Text size="xs" c="dimmed">{p.filename} · {formatFileSize(p.size_bytes)}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">{[p.requested_by_name, when(p.requested_at)].filter(Boolean).join(' · ')}</Text>
                </Table.Td>
                <Table.Td>
                  <Group justify="flex-end"><PendingUploadBadge p={p} /></Group>
                </Table.Td>
              </Table.Tr>
            ))}

            {staged.uploads.map((op) => (
              <Table.Tr key={op.tempKey} style={{ background: 'var(--mantine-color-orange-0)' }}>
                <Table.Td>
                  <Text size="sm" fw={500}>{labelOf(op.docKind)}</Text>
                  {op.title && <Text size="xs" c="dimmed">{op.title}</Text>}
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">{op.file.name} · {formatFileSize(op.file.size)}</Text>
                </Table.Td>
                <Table.Td><Text size="xs" c="dimmed">(uploads on Save)</Text></Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="nowrap" justify="flex-end">
                    <Badge color="orange" variant="light" size="xs">Pending Save</Badge>
                    {failed(op.tempKey)}
                    {active && (
                      <Tooltip label="Undo staged upload" withArrow>
                        <ActionIcon variant="light" color="gray" onClick={() => queue.unstage(op.tempKey)}>
                          <IconArrowBackUp size={14} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}

            {showRemoved && removed.map((d) => {
              const restoring = staged.byDoc.get(d.id)?.kind === 'restore';
              return (
                <Table.Tr key={d.id} style={{ opacity: restoring ? 1 : 0.55 }}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{d.kind_label}</Text>
                    {d.title && <Text size="xs" c="dimmed">{d.title}</Text>}
                  </Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{d.filename} · {formatFileSize(d.size_bytes)}</Text></Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{[d.created_by_name, when(d.created_at)].filter(Boolean).join(' · ')}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap" justify="flex-end">
                      {restoring
                        ? <Badge color="orange" variant="light" size="xs">Pending restore</Badge>
                        : <Badge color="gray" variant="light" size="xs">Removed {when(d.deleted_at)}</Badge>}
                      {failed(d.id)}
                      <PendingCRBadge crs={d.pending_change_requests} />
                      {active && (restoring ? (
                        <Tooltip label="Undo staged restore" withArrow>
                          <ActionIcon variant="light" color="gray" onClick={() => queue.unstage(d.id)}>
                            <IconArrowBackUp size={14} />
                          </ActionIcon>
                        </Tooltip>
                      ) : (
                        <Tooltip label="Restore (on Save)" withArrow>
                          <ActionIcon variant="light" color="green" aria-label={`Restore ${d.kind_label}`}
                            onClick={() => queue.stage({ kind: 'restore', document_id: d.id })}>
                            <IconRotate size={14} />
                          </ActionIcon>
                        </Tooltip>
                      ))}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}

      <AddDocumentModal
        opened={addOpen}
        onClose={() => setAddOpen(false)}
        kinds={kinds}
        onAdd={stageUpload}
      />
    </Stack>
  );
}
